// src/components/charts/BarrasHorizontalesChart.jsx
import React from 'react';
import { Card } from 'react-bootstrap';

/**
 * Componente reutilizable para barras horizontales con porcentajes
 * @param {Object} props
 * @param {String} props.title - Título del gráfico
 * @param {Array} props.data - Array de objetos con { area, porcentaje, color }
 * @param {Boolean} props.showPercentageLabel - Mostrar etiqueta de porcentaje
 * @param {String} props.maxLabel - Etiqueta para el máximo (ej: "100%")
 */
const BarrasHorizontalesChart = ({ 
  title, 
  data, 
  showPercentageLabel = true,
  maxLabel = '100%' 
}) => {
  // ✅ Validación mejorada de datos
  const validData = React.useMemo(() => {
    // Si data es null, undefined, o no es un array
    if (!data || !Array.isArray(data)) {
      console.warn('BarrasHorizontalesChart: data no es un array válido', data);
      return [];
    }
    
    // Si el array está vacío
    if (data.length === 0) {
      return [];
    }
    
    // Filtrar elementos válidos que tengan las propiedades necesarias
    return data.filter(item => 
      item && 
      (item.area || item.categoria || item.label) && 
      (item.porcentaje !== undefined || item.valor !== undefined)
    ).map(item => ({
      // Normalizar nombres de propiedades
      area: item.area || item.categoria || item.label || 'Sin categoría',
      porcentaje: parseFloat(item.porcentaje || item.valor || 0),
      color: item.color || '#3b82f6'
    }));
  }, [data]);

  // Si no hay datos válidos, mostrar mensaje
  if (validData.length === 0) {
    return (
      <Card>
        <Card.Body>
          <h5 className="mb-3">{title}</h5>
          <div className="text-center py-4">
            <p className="text-muted mb-2">📊 No hay datos disponibles</p>
            <small className="text-muted">
              {data === null || data === undefined 
                ? 'Esperando datos del servidor...' 
                : 'No se encontraron registros para el período seleccionado'}
            </small>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <h5 className="mb-4">{title}</h5>
        
        <div className="d-flex flex-column gap-4">
          {validData.map((item, idx) => (
            <div key={`${item.area}-${idx}`}>
              {/* Etiqueta y porcentaje */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="small fw-medium text-truncate" style={{ maxWidth: '70%' }}>
                  {item.area}
                </span>
                {showPercentageLabel && (
                  <span className="fw-semibold">{item.porcentaje}%</span>
                )}
              </div>

              {/* Barra de progreso */}
              <div className="position-relative" style={{ height: '24px' }}>
                {/* Fondo gris */}
                <div
                  className="position-absolute w-100 rounded"
                  style={{
                    height: '100%',
                    backgroundColor: '#e5e7eb',
                  }}
                />
                
                {/* Barra de color */}
                <div
                  className="position-absolute rounded d-flex align-items-center px-2"
                  style={{
                    height: '100%',
                    width: `${Math.min(100, Math.max(0, item.porcentaje))}%`,
                    backgroundColor: item.color,
                    transition: 'width 0.6s ease-out',
                  }}
                >
                  {/* Texto interno opcional */}
                  {item.porcentaje > 15 && (
                    <span className="text-white small fw-semibold">
                      {item.porcentaje}%
                    </span>
                  )}
                </div>

                {/* Etiqueta de máximo */}
                {maxLabel && (
                  <span
                    className="position-absolute text-muted small"
                    style={{
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    {maxLabel}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer informativo */}
        <div className="mt-3 pt-3 border-top">
          <small className="text-muted">
            Total de categorías: {validData.length}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
};

export default BarrasHorizontalesChart;